import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError, type ApiError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

const MINIMUM_JUSTIFICATION = 20;

/**
 * What a doctor sees instead of a chart they hold no relationship with.
 *
 * Two things pull against each other here, and the screen has to do both. It
 * must work in a genuine emergency, where somebody is on the floor of the
 * waiting room and the doctor has one hand free. And it must be uncomfortable
 * enough that nobody reaches for it to save walking to an administrator.
 *
 * It used to read as a form: a heading, a paragraph of lead text, a box. The
 * paragraph carried the four-hour limit, the review and the permanence of the
 * reason, which is everything that makes this consequential, and a paragraph
 * above a text box is a paragraph nobody reads twice.
 *
 * So the three facts are the content now, stated one per line, and the box is
 * underneath them. The ordinary route sits above the whole thing, because the
 * right answer most of the time is to ask somebody.
 *
 * The reason is typed, never chosen from a list, because a list turns into a
 * habit (CLN-F-041).
 */
export function BreakGlass({
  patientId,
  refusal,
  onGranted,
}: {
  patientId: string;
  refusal: ApiError;
  onGranted: () => void;
}) {
  const [justification, setJustification] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const written = justification.trim().length;
  const tooShort = written < MINIMUM_JUSTIFICATION;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const { error: failed } = await api.POST("/portal/patients/{id}/break-glass", {
      params: { path: { id: patientId }, header: clinicHeader() },
      body: { justification: justification.trim() },
    });
    setBusy(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onGranted();
  }

  return (
    <section className="gate">
      <Link to="/patients" className="back">
        Patients
      </Link>

      <h1>This chart is not open to you</h1>
      <p className="gate-reason">{refusal.message}</p>

      {/* The ordinary route, first and in full. Most of the time the answer is
          that somebody assigns the patient, and a screen that leads with the
          emergency control is a screen that teaches people to use it. */}
      <div className="gate-ordinary">
        <h2>The usual way</h2>
        <p>
          A clinic administrator can record you as treating this patient, and
          then the chart opens normally. If somebody is available to ask, ask
          them.
        </p>
      </div>

      <form onSubmit={submit} className="gate-emergency">
        <div className="gate-emergency-head">
          <h2>Emergency access</h2>
          <p>For when there is nobody to ask and you need the chart now.</p>
        </div>

        {/* The three facts, before the box rather than above it in a
            paragraph. This is what the doctor is signing. */}
        <ul className="gate-terms">
          <li>
            <strong>It lasts four hours.</strong> It ends on its own. You do not
            close it and you cannot extend it.
          </li>
          <li>
            <strong>Every grant is reviewed.</strong> Your clinic sees that you
            opened this chart, and when.
          </li>
          <li>
            <strong>What you write is permanent.</strong> Your name and your
            reason are kept with the record. Neither can be edited or removed
            afterwards.
          </li>
        </ul>

        <label htmlFor="justification">Why do you need this chart now?</label>
        <textarea
          id="justification"
          rows={3}
          value={justification}
          onChange={(event) => setJustification(event.target.value)}
          placeholder="For example: patient collapsed in the waiting room, no doctor on file."
        />
        {/* A live count, and what it is for beside it. The number alone tells
            somebody they are short; it does not tell them the sentence is
            going to be read by a person. */}
        <p
          className={tooShort ? "gate-count short" : "gate-count"}
          aria-live="polite"
        >
          <span className="gate-count-n">
            {written} / {MINIMUM_JUSTIFICATION}
          </span>
          {tooShort
            ? ` — ${MINIMUM_JUSTIFICATION - written} more before you can open it.`
            : " — this is what the reviewer will read."}
        </p>

        <ErrorNote message={error} />
        <button type="submit" className="danger" disabled={busy || tooShort}>
          {busy ? "Opening…" : "Open emergency access"}
        </button>
      </form>
    </section>
  );
}
